import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { fetchTermsAccepted, recordFirstLoginConsents } from '@/lib/consentLog';

/**
 * First-login consent gate (29 JUN 2026).
 *
 * The app is invite-only — there is no self-serve signup screen to attach a
 * consent checkbox to. So consent is captured at the FIRST authenticated entry:
 * we check the consent ledger for a Terms acceptance at the current document
 * version, and if absent show a one-time blocking step requiring the user to
 * accept the Terms, Privacy Policy and personal-data consent. Acceptance writes
 * the ledger rows (and backfills the current cookie choice).
 *
 * Mounted inside AuthGate, so it only exists for an authenticated user and
 * remounts per identity. Fails open on a transient lookup error (never traps a
 * user behind a network blip). Legal links open in a new tab so reading a
 * document does not dismiss the gate.
 */

type Phase = 'checking' | 'needed' | 'ok';

export function ConsentGate() {
  const { user, logout } = useAuth();
  const userId = user?.id ?? null;
  const email = user?.email ?? null;
  const [phase, setPhase] = useState<Phase>('checking');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!userId) {
      setPhase('ok');
      return;
    }
    setPhase('checking');
    void fetchTermsAccepted(userId).then((res) => {
      if (!active) return;
      // res === false → not yet accepted → gate. true/null (already accepted or
      // transient error) → don't block.
      setPhase(res === false ? 'needed' : 'ok');
    });
    return () => {
      active = false;
    };
  }, [userId]);

  if (phase !== 'needed' || !userId) return null;

  const accept = async () => {
    if (!agreed) {
      setError('Отметь согласие, чтобы продолжить.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const ok = await recordFirstLoginConsents(userId, email);
    setSubmitting(false);
    if (!ok) {
      setError('Не удалось сохранить согласие. Попробуй ещё раз.');
      return;
    }
    setPhase('ok');
  };

  return (
    <div
      className="consent-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-gate-title"
    >
      <div className="consent-gate__card">
        <h2 id="consent-gate-title" className="consent-gate__title">
          Прежде чем начать
        </h2>
        <p className="consent-gate__lead">
          Чтобы пользоваться QA Studio, прими условия и подтверди согласие на обработку данных.
        </p>
        <label className="auth-consent consent-gate__check">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            data-testid="consent-gate-check"
          />
          <span>
            Я принимаю{' '}
            <a href="#/terms" target="_blank" rel="noopener noreferrer">
              Пользовательское соглашение
            </a>{' '}
            и{' '}
            <a href="#/privacy" target="_blank" rel="noopener noreferrer">
              Политику конфиденциальности
            </a>{' '}
            и даю{' '}
            <a href="#/consent" target="_blank" rel="noopener noreferrer">
              согласие на обработку персональных данных
            </a>
            .
          </span>
        </label>
        {error && (
          <p className="consent-gate__error" role="alert">
            {error}
          </p>
        )}
        <div className="consent-gate__actions">
          <button
            type="button"
            className="cookie-btn cookie-btn--ghost"
            onClick={() => {
              void logout();
            }}
          >
            Выйти
          </button>
          <button
            type="button"
            className="cookie-btn cookie-btn--primary"
            disabled={submitting || !agreed}
            onClick={() => {
              void accept();
            }}
          >
            {submitting ? 'Сохраняем…' : 'Принять и продолжить'}
          </button>
        </div>
      </div>
    </div>
  );
}
